import { useEffect, useState } from "react";
import { BookOpen, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const WINDOW_DAYS = 7;

interface LatestMagazine {
  id: string;
  title: string;
  year: number;
  start_month: number;
  end_month: number;
  url: string;
  daysLeft: number;
}

export const MagazineRibbon = () => {
  const [magazine, setMagazine] = useState<LatestMagazine | null>(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const since = new Date(Date.now() - WINDOW_DAYS * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from("magazines")
        .select("id,title,year,start_month,end_month,pdf_path,created_at,updated_at")
        .eq("published", true)
        .gte("updated_at", since)
        .order("updated_at", { ascending: false })
        .limit(1);

      if (cancelled) return;
      const row: any = data?.[0];
      if (!row?.pdf_path) return;

      const publishedAt = new Date(row.updated_at || row.created_at).getTime();
      const daysLeft = Math.max(
        1,
        WINDOW_DAYS - Math.floor((Date.now() - publishedAt) / (24 * 60 * 60 * 1000)),
      );
      const { data: pub } = supabase.storage.from("general-media").getPublicUrl(row.pdf_path);

      setMagazine({
        id: row.id,
        title: row.title,
        year: row.year,
        start_month: row.start_month,
        end_month: row.end_month,
        url: pub.publicUrl,
        daysLeft,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!magazine || !open) return null;

  const period =
    magazine.start_month === magazine.end_month
      ? `${MONTHS[magazine.start_month - 1]} ${magazine.year}`
      : `${MONTHS[magazine.start_month - 1]}–${MONTHS[magazine.end_month - 1]} ${magazine.year}`;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -40, opacity: 0 }}
        className="relative bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 text-white py-2.5 px-4 shadow-md z-40"
      >
        <div className="container mx-auto flex items-center justify-center gap-2 sm:gap-3 text-center pr-8">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/25 shrink-0">
            <BookOpen className="w-4 h-4" />
          </span>
          <a
            href="/keep-in-touch"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-1 hover:underline underline-offset-4"
          >
            <span className="text-xs sm:text-sm font-bold tracking-wide uppercase">
              New KIT Magazine out now — {magazine.title}
            </span>
            <span className="hidden sm:inline text-xs sm:text-sm font-medium">· {period}</span>
            <span className="inline-flex items-center gap-1 text-xs sm:text-sm font-semibold ml-1 bg-white/25 rounded-full px-2.5 py-0.5 group-hover:bg-white/35 transition-colors">
              Read now <ArrowRight className="w-3 h-3" />
            </span>
          </a>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 hover:bg-white/20 rounded-full p-1"
            aria-label="Dismiss magazine announcement"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MagazineRibbon;
